import { objectToFormData, downloadBlob } from "../helpers/ApiHelper";
import { AuthContextModel } from "../App";
import { IPagination } from "soprasteria-ui-library/dist/models/IPagination";
export interface IPaginatedResponse<T> {
    paginationHeader: IPagination;
    data: T;
}

export class ApiClient {
    accessToken: string;
    userRolesGroup: string;

    constructor(auth: AuthContextModel) {
        this.accessToken = auth.user?.access_token || '';
        this.userRolesGroup = auth.userRolesGroup || '';
    }

    public buildHeader(accessToken: string, userRolesGroup: string): Headers {
        return new Headers({
            'Authorization': 'Bearer ' + accessToken,
            'RolesGroup': userRolesGroup
        });
    }

    /**
     * Performs an HTTP-GET call to fetch a resource. 
     * 
     * @template T Response data type.
     * @param url HTTP URL to resource, including ID.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public get<T>(url: string, success: (response: T) => void, error?: (message: string) => void, final?: () => void) {
        fetch(url, {
            method: 'GET',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup)
        }).then(response => {
            if (response.ok) {
                response.json().then(success);
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }

    /**
     * Performs an HTTP-GET call and downloads the file response.
     * 
     * @param url HTTP URL to resource, including ID.
     * @param fileName Name of the file to download.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public getFile(url: string, fileName?: string, success?: () => void, error?: (message: string) => void, final?: () => void) {
        fetch(url, {
            method: 'GET',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup)
        }).then(response => {
            if (response.ok) {
                response.blob()
                    .then(blob => {
                        let fileDownloadName = fileName;
                        const fileNameHeader = response.headers.get("content-disposition")?.split('; ')[1];

                        if (fileName) {
                            fileDownloadName = fileName
                        } else if (fileNameHeader) {
                            fileDownloadName = fileNameHeader.split('=')[1];
                        }
                        else {
                            fileDownloadName = " "
                        }
                        downloadBlob(blob, fileDownloadName)
                    })
                    .then(() => {
                        if (success) {
                            success();
                        }
                    });
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }

    /**
     * Performs an HTTP-GET call and returns a resource within a pagination wrapper.
     * 
     * @template T Response data type.
     * @param url HTTP URL to resource, including query.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public getPaginated<T>(url: string, success: (response: IPaginatedResponse<T>) => void, error?: (message: string) => void, final?: () => void) {
        fetch(url, {
            method: 'GET',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup)
        }).then(async response => {
            if (response.ok) {
                let paginatedResponse = {} as IPaginatedResponse<T>;
                let paginationHeader = response.headers.get('x-pagination');
                if (paginationHeader !== null) {
                    paginatedResponse.paginationHeader = JSON.parse(paginationHeader);
                }
                paginatedResponse.data = await response.json();
                if (success !== undefined) {
                    success(paginatedResponse);
                }
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }

    /**
     * Performs an HTTP-POST call for creating or updating a resource.
     * 
     * @param url HTTP URL to resource, including ID if updating existing resource.
     * @param data Item to create/update.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public post(url: string, data: any, success?: () => void, error?: (message: string) => void, final?: () => void) {
        const formData = objectToFormData(data);
        fetch(url, {
            method: 'POST',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup),
            body: formData
        }).then((response) => {
            if (response.ok) {
                if (success) {
                    success();
                }
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }

    /**
     * Performs an HTTP-POST call for creating or updating a resource.
     * 
     * @template T Response data type.
     * @param url HTTP URL to resource, including ID if updating existing resource.
     * @param data Item to create/update.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public postWithResponse<T>(url: string, data: any, success: (response: T) => void, error?: (message: string) => void, final?: () => void) {
        const formData = objectToFormData(data);
        fetch(url, {
            method: 'POST',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup),
            body: formData
        }).then((response) => {
            if (response.ok) {
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.indexOf("application/json") !== -1) {
                    response.json().then(success);
                } else {
                    response.text().then(success as any);
                }
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }

    /**
     * Performs an HTTP-PUT call for creating or updating a resource.
     * 
     * @param url HTTP URL to resource, including ID if updating existing resource.
     * @param data Item to create/update.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public put(url: string, data: any, success: () => void, error?: (message: string) => void, final?: () => void) {
        const formData = objectToFormData(data);
        fetch(url, {
            method: 'PUT',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup),
            body: formData
        }).then((response) => {
            if (response.ok) {
                success();
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }

    /**
     * Performs an HTTP-DELETE call for deleting a resource.
     * 
     * @param url HTTP URL to resource, including ID.
     * @param success Function that runs on success.
     * @param error Function that runs on error.
     * @param final Function that always runs after the API-call.
     */
    public delete(url: string, success: () => void, error?: (message: string) => void, final?: () => void) {
        fetch(url, {
            method: 'DELETE',
            headers: this.buildHeader(this.accessToken, this.userRolesGroup),
        }).then(response => {
            if (response.ok) {
                success();
            }
            else if (error) {
                response.text().then(error);
            }
        }).finally(() => {
            if (final) {
                final();
            }
        }).catch(err => console.log("Error: " + err));
    }
}


